"use client";

import { useLocale } from "@i18n/react";
import { useQueryClient } from "@tanstack/react-query";

import type { paths as OpenApiPaths } from "../../../../request/generated/schema";
import type { UseRequestMutationProps } from "../../../../request/hooks/useRequestMutation";
import { useRequestMutation } from "../../../../request/hooks/useRequestMutation";
import { keyCommunityBroadcasts } from "./keyCommunityBroadcasts";

export type UseRequestCommunityBroadcastDeleteVariables = {
  broadcastId: number;
};

const communityBroadcastsPath =
  "/communities/{community_id}/broadcasts" as const;

const communityBroadcastPath =
  "/communities/{community_id}/broadcasts/{broadcast_id}" as const;

/**
 * POST body — source of truth from the OpenAPI schema. `scheduled_at` is an
 * ISO-8601 instant to send at; omit it to create and deliver immediately. A
 * time in the past is rejected with
 * {@link BROADCAST_CANNOT_BE_SCHEDULED_IN_PAST}.
 */
export type UseRequestCommunityBroadcastPostVariables =
  OpenApiPaths[typeof communityBroadcastsPath]["post"]["requestBody"]["content"]["application/json"];

/**
 * PUT body — source of truth from the OpenAPI schema — plus the id it is sent
 * to. Every field replaces rather than merges, and omitting one leaves it
 * alone, so the three `scheduled_at` cases stay distinct on the wire:
 * `JSON.stringify` keeps `null` (send now) and drops `undefined` (no change).
 */
export type UseRequestCommunityBroadcastPutVariables = {
  broadcastId: number;
} & OpenApiPaths[typeof communityBroadcastPath]["put"]["requestBody"]["content"]["application/json"];

/** `BroadcastCannotBeScheduledInPast` — 400 from a `scheduled_at` in the past. */
export const BROADCAST_CANNOT_BE_SCHEDULED_IN_PAST = 12067;

/**
 * `BroadcastNotScheduled` — 409. The broadcast has already gone out, including
 * when the poller wins the race between reading the list and writing the edit.
 */
export const BROADCAST_NOT_SCHEDULED = 12068;

/** `UserNotBroadcastCreator` — 403 from editing someone else's broadcast. */
export const USER_NOT_BROADCAST_CREATOR = 12069;

export type UseRequestCommunityBroadcastProps = {
  communityId: number;
  mutationDeleteProps?: UseRequestMutationProps<
    any,
    UseRequestCommunityBroadcastDeleteVariables
  >;
  mutationPostProps?: UseRequestMutationProps<
    any,
    UseRequestCommunityBroadcastPostVariables
  >;
  mutationPutProps?: UseRequestMutationProps<
    any,
    UseRequestCommunityBroadcastPutVariables
  >;
};

export const useRequestCommunityBroadcast = ({
  communityId,
  mutationDeleteProps,
  mutationPostProps,
  mutationPutProps,
}: UseRequestCommunityBroadcastProps) => {
  const locale = useLocale();
  const queryClient = useQueryClient();

  return {
    mutationDelete: useRequestMutation({
      ...mutationDeleteProps,
      mutationFnRequestProps: ({ broadcastId }) => ({
        method: "DELETE",
        urlBackendPath: `/communities/${communityId}/broadcasts/${broadcastId}`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunityBroadcasts({ id: communityId }), locale],
        });

        if (mutationDeleteProps?.onSuccess)
          mutationDeleteProps.onSuccess(data, variables, context);
      },
    }),
    mutationPost: useRequestMutation({
      ...mutationPostProps,
      mutationFnRequestProps: (data) => ({
        body: JSON.stringify(data),
        method: "POST",
        urlBackendPath: `/communities/${communityId}/broadcasts`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunityBroadcasts({ id: communityId }), locale],
        });

        if (mutationPostProps?.onSuccess)
          mutationPostProps.onSuccess(data, variables, context);
      },
    }),
    mutationPut: useRequestMutation({
      ...mutationPutProps,
      mutationFnRequestProps: ({ broadcastId, ...body }) => ({
        body: JSON.stringify(body),
        method: "PUT",
        urlBackendPath: `/communities/${communityId}/broadcasts/${broadcastId}`,
      }),
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({
          queryKey: [keyCommunityBroadcasts({ id: communityId }), locale],
        });

        if (mutationPutProps?.onSuccess)
          mutationPutProps.onSuccess(data, variables, context);
      },
    }),
  };
};
